import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useTranslation } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AntPlusDeviceCombobox from "@/components/AntPlusDeviceCombobox";
import { toast } from "sonner";

export default function PublicIntake() {
  const { token = "" } = useParams();
  const t = useTranslation();
  const [organizationName, setOrganizationName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    birthDate: "",
    device: "",
    consentAccepted: false,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const { data } = await api.get(`/public/intake/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setOrganizationName(data.organizationName);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(extractApiError(err, t("publicIntake.loadError")));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (token) {
      load();
    } else {
      setLoading(false);
      setLoadError(t("publicIntake.missingToken"));
    }

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.consentAccepted) {
      toast.error(t("publicIntake.toastConsent"));
      return;
    }
    if (!form.device.trim()) {
      toast.error(t("publicIntake.toastDevice"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/public/intake/${encodeURIComponent(token)}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        birthDate: form.birthDate,
        device: form.device.trim(),
        consentAccepted: true,
      });
      setSubmitted(true);
      toast.success(t("publicIntake.toastSubmitted"));
    } catch (err) {
      toast.error(extractApiError(err, t("publicIntake.submitError")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-xl flex-col justify-center space-y-6">
        <div className="space-y-3">
          <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="public-intake-title">
            {loading
              ? t("common.misc.loading")
              : t("publicIntake.title", { organizationName: organizationName || t("publicIntake.fallbackTitle") })}
          </h1>
          {!loading && !loadError && !submitted && (
            <div className="space-y-3 text-base leading-7 text-muted-foreground">
              <p>{t("publicIntake.intro1")}</p>
              <p>{t("publicIntake.intro2", { organizationName })}</p>
            </div>
          )}
        </div>

        {loadError ? (
          <section
            className="rounded-lg border border-border bg-card p-6 text-sm text-destructive shadow-sm"
            data-testid="public-intake-error"
          >
            {loadError}
          </section>
        ) : submitted ? (
          <section
            className="rounded-lg border border-border bg-card p-6 shadow-sm"
            data-testid="public-intake-success"
          >
            <p className="font-display text-xl tracking-tight">{t("publicIntake.successTitle")}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{t("publicIntake.success")}</p>
          </section>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
            data-testid="public-intake-form"
          >
            <div className="space-y-2">
              <Label className="text-xs">{t("publicIntake.fullName")}</Label>
              <Input
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                required
                disabled={loading || submitting}
                data-testid="public-intake-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("publicIntake.email")}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => update("email", event.target.value)}
                required
                disabled={loading || submitting}
                data-testid="public-intake-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("publicIntake.birthDate")}</Label>
              <Input
                type="date"
                value={form.birthDate}
                onChange={(event) => update("birthDate", event.target.value)}
                required
                disabled={loading || submitting}
                data-testid="public-intake-birth-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("publicIntake.device")}</Label>
              <AntPlusDeviceCombobox
                value={form.device}
                onChange={(value) => update("device", value)}
                disabled={loading || submitting}
                required
                labels={{
                  placeholder: t("publicIntake.devicePlaceholder"),
                  searchPlaceholder: t("publicIntake.deviceSearch"),
                  noResults: t("publicIntake.deviceNoResults"),
                  notInList: t("publicIntake.deviceNotInList"),
                  customPlaceholder: t("publicIntake.deviceCustomPlaceholder"),
                }}
                testId="public-intake-device"
              />
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="public-intake-consent"
                  checked={form.consentAccepted}
                  onCheckedChange={(checked) => update("consentAccepted", checked === true)}
                  disabled={loading || submitting}
                  data-testid="public-intake-consent"
                />
                <Label
                  htmlFor="public-intake-consent"
                  className="text-sm leading-6 text-foreground"
                >
                  {t("publicIntake.consent")}{" "}
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-4"
                    data-testid="public-intake-terms-link"
                  >
                    {t("publicIntake.termsLink")}
                  </Link>{t("publicIntake.consentSuffix")}
                </Label>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full rounded-lg"
              disabled={loading || submitting}
              data-testid="public-intake-submit"
            >
              {submitting ? t("publicIntake.submitting") : t("publicIntake.submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
